import { useParams } from "react-router";
import { meldungsIdAnzeige } from "../design/Kennung";
import { Symbol } from "../design/Symbol";
import { useDokument, useSammlung } from "../plattform/lesemodelle";
import { nachReihenfolge, useEigeneLage, useRevierteil, wert, type Rahmen } from "./daten";

/**
 * Ansicht „Jagdstatistik“ (oberflaeche.md §3.2, §6; F-181 … F-183): Kennzahlen als Zeilen mit `rowheader` = Kennzahl
 * (wörtlich wie im Lesemodell) und `cell` = Wert, nach Themen gruppiert; die Schüsse auf Rot- und Damwild je Altersklasse;
 * `region` „Einzelmeldungen“ (jede Meldung mit ihrem Schützen, keine Auswertung je Schütze). Beim Veranstalter zusätzlich
 * die Statistik gesamt je Revier. Alles über Listener: neue Meldungen erscheinen ohne Neuladen (F-182).
 */

const GRUPPEN: Array<{ name: string; symbol: string; passt: (k: string) => boolean }> = [
  { name: "Schüsse", symbol: "schuss", passt: (k) => k.startsWith("Schüsse") },
  { name: "Erlegungen und Bergung", symbol: "bergung", passt: (k) => k.startsWith("Erlegungen") },
  { name: "Suchen", symbol: "nachsuche", passt: (k) => k.startsWith("Nachsuchen") || k.startsWith("Kontrollsuchen") },
  { name: "Kontingente", symbol: "kontingent", passt: (k) => k.startsWith("Kontingent ") },
  { name: "Anstellung", symbol: "stand", passt: (k) => k === "Schützen am Stand" },
];

const ORDNUNG = [
  "Schüsse gesamt", "Schüsse mit Stück liegt", "Schüsse mit Anschuss", "Schüsse mit Fehlschuss", "Schüsse auf Raubwild", "Schüsse auf Rehwild",
  "Schüsse auf Damwild", "Schüsse auf Rotwild", "Schüsse auf Schwarzwild", "Erlegungen", "Erlegungen liegt", "Erlegungen zur Bergung markiert",
  "Erlegungen geborgen", "Nachsuchen zugewiesen", "Nachsuchen gefunden", "Nachsuchen erlegt", "Nachsuchen nicht gefunden",
  "Kontrollsuchen erforderlich", "Kontrollsuchen zugewiesen", "Kontrollsuchen nicht gefunden", "Kontrollsuchen nicht erforderlich", "Schützen am Stand",
];
const KONTINGENT = ["Stückzahl", "erlegt", "Rest", "offene Anschüsse", "Status"];

/** Reihenfolge der Kennzahlen (lesemodelle.md §4), Kontingente nach Name und dann wie im Kontingentstand. */
function vergleichen(a: string, b: string): number {
  const ka = KONTINGENT.find((s) => a.startsWith("Kontingent ") && a.endsWith(` ${s}`));
  const kb = KONTINGENT.find((s) => b.startsWith("Kontingent ") && b.endsWith(` ${s}`));
  if (ka && kb) {
    const na = a.slice(0, -ka.length);
    const nb = b.slice(0, -kb.length);
    return na === nb ? KONTINGENT.indexOf(ka) - KONTINGENT.indexOf(kb) : na.localeCompare(nb, "de");
  }
  const ia = ORDNUNG.indexOf(a);
  const ib = ORDNUNG.indexOf(b);
  return (ia < 0 ? ORDNUNG.length : ia) - (ib < 0 ? ORDNUNG.length : ib);
}

function Tabelle({ spalten, zeilen }: { spalten: string[]; zeilen: unknown[][] }) {
  return (
    <table className="tabelle">
      <thead><tr>{spalten.map((s) => <th key={s} scope="col">{s}</th>)}</tr></thead>
      <tbody>{zeilen.map((z, i) => <tr key={i}>{z.map((w, j) => <td key={j}>{wert(w)}</td>)}</tr>)}</tbody>
    </table>
  );
}

export function Jagdstatistik({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const { revier } = useEigeneLage(id);
  const kennzahlen = useDokument(revier ? `drueckjagden/${id}/reviere/${revier}/jagdstatistik/kennzahlen` : null).daten;
  const altersklassen = useRevierteil(id, revier, "jagdstatistik_altersklassen");
  const einzelmeldungen = useRevierteil(id, revier, "jagdstatistik_einzelmeldungen");
  const gesamt = nachReihenfolge(useSammlung(`drueckjagden/${id}/jagdstatistik_gesamt`).daten);
  const namen = Object.keys(kennzahlen ?? {}).filter((k) => !k.startsWith("_")).sort(vergleichen);
  return rahmen({
    drueckjagdId: id, name: "Jagdstatistik",
    children: (
      <>
        {GRUPPEN.map((g) => {
          const zeilen = namen.filter(g.passt);
          if (!zeilen.length) return null;
          return (
            <section key={g.name} className="liste-bereich platte" role="region" aria-label={g.name}>
              <h2 className="kopf"><Symbol name={g.symbol} />{g.name}</h2>
              <table className="tabelle">
                <tbody>
                  {zeilen.map((k) => <tr key={k}><th scope="row">{k}</th><td>{wert(kennzahlen?.[k])}</td></tr>)}
                </tbody>
              </table>
            </section>
          );
        })}
        {altersklassen.length ? (
          <section className="liste-bereich platte" role="region" aria-label="Schüsse je Altersklasse">
            <h2 className="kopf"><Symbol name="rotwild" />Schüsse je Altersklasse</h2>
            <Tabelle spalten={["Wildart", "Altersklasse", "Schüsse"]} zeilen={altersklassen.map((z) => [z["Wildart"], z["Altersklasse"], z["Schüsse"]])} />
          </section>
        ) : null}
        <section className="liste-bereich platte" role="region" aria-label="Einzelmeldungen">
          <h2 className="kopf"><Symbol name="schuss" />Einzelmeldungen</h2>
          <Tabelle spalten={["Schuss", "Schütze", "Stand", "Zeitpunkt", "Ansprache", "Schussausgang"]}
            zeilen={einzelmeldungen.map((z) => [meldungsIdAnzeige(z["Schuss"]), z["Schütze"], z["Stand"], z["Zeitpunkt"], z["Ansprache"], z["Schussausgang"]])} />
        </section>
        {gesamt.length ? (
          <section className="liste-bereich platte" role="region" aria-label="Jagdstatistik gesamt">
            <h2 className="kopf"><Symbol name="lagebild" />Jagdstatistik gesamt</h2>
            <Tabelle spalten={["Revier", "Schüsse gesamt", "Stück liegt", "Anschuss", "Fehlschuss", "Erlegungen"]}
              zeilen={gesamt.map((z) => [z["Revier"], z["Schüsse gesamt"], z["Stück liegt"], z["Anschuss"], z["Fehlschuss"], z["Erlegungen"]])} />
          </section>
        ) : null}
      </>
    ),
  });
}
